import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { getScenarioDisplayName } from "./laborModelPublicationStatus";


const DAY_INDEX_BY_NAME = {
  sunday: 0,
  monday: 1,
  tuesday: 2,
  wednesday: 3,
  thursday: 4,
  friday: 5,
  saturday: 6,
};


function dateForApi(value) {
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, "0");
  const day = String(value.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}


function nextWeekStartForApi(weekStartDay) {
  const value = new Date();
  const targetDay = DAY_INDEX_BY_NAME[weekStartDay] ?? 1;
  value.setDate(value.getDate() + ((targetDay - value.getDay() + 7) % 7));
  return dateForApi(value);
}


function isWeekStartDate(value, weekStartDay) {
  if (!value) return false;
  const parsed = new Date(`${value}T00:00:00`);
  return parsed.getDay() === (DAY_INDEX_BY_NAME[weekStartDay] ?? 1);
}


function createIdempotencyKey() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `labor-publication-${Date.now()}`;
}

function formatDisplayDate(value) {
  if (!value) return "the selected date";
  return new Date(`${value}T00:00:00`).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}


function PublishLaborModelWarningModal({
  open,
  handleClose,
  publish_labor_standard,
  weekStartDay = "monday",
  selectedScenario,
  changeCount,
  evaluation,
  onEvaluate,
  evaluating = false,
}) {
  const [comment, setComment] = useState("");
  const [publicationName, setPublicationName] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(
    nextWeekStartForApi(weekStartDay)
  );
  const [idempotencyKey, setIdempotencyKey] = useState(createIdempotencyKey());
  const [isPublishing, setIsPublishing] = useState(false);
  const weekStartLabel =
    weekStartDay.charAt(0).toUpperCase() + weekStartDay.slice(1);

  useEffect(() => {
    if (open) {
      setComment("");
      setPublicationName(getScenarioDisplayName(selectedScenario).slice(0, 255));
      setEffectiveDate(nextWeekStartForApi(weekStartDay));
      setIdempotencyKey(createIdempotencyKey());
      setIsPublishing(false);
    }
  }, [open, selectedScenario, weekStartDay]);

  const effectiveDateIsValid = isWeekStartDate(effectiveDate, weekStartDay);
  const effectiveDateValue = effectiveDate
    ? new Date(`${effectiveDate}T00:00:00`)
    : null;
  const evaluationIsCurrent =
    evaluation &&
    Number(evaluation.revision) === Number(selectedScenario?.revision);
  const normalizedChangeCount = Number(changeCount);
  const hasNoModelChanges =
    changeCount !== null &&
    changeCount !== undefined &&
    Number.isFinite(normalizedChangeCount) &&
    normalizedChangeCount === 0;
  const needsCurrentEvaluation = !hasNoModelChanges && !evaluationIsCurrent;
  const normalizedPublicationName = publicationName.trim();
  const evaluationMessage = evaluation
    ? "This sandbox scenario changed after its last evaluation. You can review the latest impact before publishing."
    : "This sandbox scenario has not been evaluated yet. You can optionally review its impact before publishing.";

  const publish = async () => {
    setIsPublishing(true);
    try {
      await publish_labor_standard({
        name: normalizedPublicationName,
        effectiveDate,
        idempotencyKey,
        comment: comment.trim(),
      });
      setIdempotencyKey(createIdempotencyKey());
      handleClose();
    } catch (error) {
      // The parent displays the request error. Keep this modal and key intact
      // so an ambiguous network failure can safely replay the same request.
    } finally {
      setIsPublishing(false);
    }
  };

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "calc(100% - 32px)",
    maxWidth: 600,
    maxHeight: "calc(100% - 32px)",
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
    p: 4,
    overflowY: "auto",
  };

  return (
    <Modal
      open={open}
      onClose={isPublishing ? undefined : handleClose}
      aria-labelledby="publish-labor-model-title"
      aria-describedby="publish-labor-model-description"
    >
      <Box sx={style}>
        <button
          type="button"
          className="absolute right-3 top-3 rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600"
          onClick={handleClose}
          disabled={isPublishing}
          aria-label="Close publish dialog"
        >
          <CloseIcon />
        </button>

        <div className="flex items-center space-x-2 pr-10 text-slate-900">
          <WarningIcon className="text-amber-700" />
          <h3 id="publish-labor-model-title" className="text-xl font-semibold">
            Publish to production?
          </h3>
        </div>
        <p
          id="publish-labor-model-description"
          className="mt-2 text-sm text-slate-600"
        >
          This creates a read-only published model for the entire company.
        </p>

        {needsCurrentEvaluation ? (
          <div
            className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4"
            role="status"
          >
            <p className="text-sm font-semibold text-amber-900">
              Optional impact review
            </p>
            <p className="mt-1 text-sm text-amber-800">{evaluationMessage}</p>
            {onEvaluate ? (
              <button
                type="button"
                className="mt-3 rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-600 disabled:cursor-wait disabled:opacity-60"
                onClick={() => {
                  handleClose();
                  onEvaluate();
                }}
                disabled={evaluating || isPublishing}
              >
                {evaluating ? "Evaluating…" : "Evaluate now"}
              </button>
            ) : null}
          </div>
        ) : hasNoModelChanges ? (
          <div
            className="mt-5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"
            role="status"
          >
            This scenario has 0 model changes. You can publish it without an
            impact evaluation to reapply the current model from the selected
            effective date.
          </div>
        ) : (
          <div
            className="mt-5 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900"
            role="status"
          >
            <span className="h-2 w-2 rounded-full bg-emerald-600" />
            Impact evaluation is up to date.
          </div>
        )}

        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              Published model name
            </span>
            <input
              type="text"
              className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-violet-600 focus:outline-none focus:ring-4 focus:ring-violet-100"
              value={publicationName}
              onChange={(event) => setPublicationName(event.target.value)}
              maxLength={255}
              disabled={isPublishing}
              aria-label="Published model name"
            />
            <span className="mt-1 block text-xs text-slate-500">
              This name identifies the published model in history.
            </span>
          </label>
          <div className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              Effective date
            </span>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DesktopDatePicker
                value={effectiveDateValue}
                format="MM/dd/yyyy"
                onChange={(newValue) => {
                  if (!newValue || Number.isNaN(newValue.getTime())) {
                    setEffectiveDate("");
                    return;
                  }
                  setEffectiveDate(dateForApi(newValue));
                }}
                shouldDisableDate={(dateValue) =>
                  dateValue.getDay() !==
                  (DAY_INDEX_BY_NAME[weekStartDay] ?? 1)
                }
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    sx: {
                      marginTop: "4px",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        backgroundColor: "#fff",
                        fontSize: "14px",
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#cbd5e1",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#94a3b8",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d28d9",
                          borderWidth: "1px",
                        },
                        "&.Mui-focused": {
                          boxShadow: "0 0 0 4px rgba(109, 40, 217, 0.08)",
                        },
                      },
                      "& .MuiInputBase-input": {
                        paddingTop: "10px",
                        paddingBottom: "10px",
                      },
                    },
                  },
                  day: {
                    sx: {
                      "&.MuiPickersDay-root.Mui-selected": {
                        backgroundColor: "#6d28d9",
                        "&:hover, &:focus": {
                          backgroundColor: "#5b21b6",
                        },
                      },
                    },
                  },
                  actionBar: {
                    actions: ["clear", "accept"],
                  },
                  popper: {
                    sx: { zIndex: 1500 },
                  },
                }}
              />
            </LocalizationProvider>
            <span
              className={`mt-1 block text-xs ${
                effectiveDateIsValid ? "text-slate-500" : "text-red-600"
              }`}
            >
              Choose a {weekStartLabel}, the company&apos;s business week start day.
            </span>
          </div>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              Publish note (optional)
            </span>
            <textarea
              className="mt-1 min-h-[88px] w-full rounded-xl border border-slate-300 px-3 py-2 text-sm focus:border-violet-600 focus:outline-none focus:ring-4 focus:ring-violet-100"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              placeholder="Summarize what changed in this release."
              maxLength={2000}
            />
            <span className="mt-1 block text-xs text-slate-500">
              This note will appear with the model in published history.
            </span>
          </label>
        </div>

        <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-sm font-semibold text-slate-900">
            Goes into effect {formatDisplayDate(effectiveDate)}
          </p>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-slate-600">
            <li>Forecasted labor recalculates where forecast data exists.</li>
            <li>Earned labor recalculates where actual-sales data exists.</li>
            <li>Finalized dates stay unchanged.</li>
          </ul>
        </div>

        <div className="mt-6 flex flex-wrap justify-end gap-3">
          <button
            type="button"
            className="rounded-lg border border-slate-300 bg-white px-5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600"
            onClick={handleClose}
            disabled={isPublishing}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-lg bg-violet-700 px-5 py-2 text-sm font-semibold text-white hover:bg-violet-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-600 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300"
            disabled={
              !selectedScenario?.uuid ||
              !normalizedPublicationName ||
              !effectiveDateIsValid ||
              isPublishing
            }
            onClick={publish}
          >
            {isPublishing ? "Publishing…" : "Publish model"}
          </button>
        </div>
      </Box>
    </Modal>
  );
}

export default PublishLaborModelWarningModal;
